import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listContracts, listClients, listInterventions, createContract, updateContract, deleteContract, duplicateContract, completeContract, clearContractPlan,
  createClient, aiAnalyzeContractPdf,
} from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Plus, FileText, Trash2, Edit3, CheckCircle2, RotateCcw, Upload, Loader2, FileCode, FileDown, ArrowUpDown, Users, Calendar, Copy, Layers, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { exportFatturaPaUrl, contractsTemplateCsvUrl, importContractsCsv, planMatrixUrl } from "../lib/api";

const empty = {
  numero_preventivo: "", client_id: "", title: "", short_description: "", total_days: 1, daily_rate: 0, intervention_type: "consulenza",
  max_per_month: null,
  priority: "medium", start_date: "", deadline: "", signed_date: "", notes: "",
  must_be_consecutive: false,
};

const priorityBadge = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-blue-50 text-blue-700 border-blue-200",
};

const statusBadge = {
  active: "bg-slate-100 text-slate-700",
  planned: "bg-blue-50 text-blue-700",
  completed: "bg-emerald-50 text-emerald-700",
  invoiced: "bg-purple-50 text-purple-700",
};

const isDeadlineNear = (c) => {
  if (!c.deadline || c.status === "completed" || c.status === "invoiced") return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(`${c.deadline}T00:00:00`) - today) / 86400000);
  return days < 15;
};


const groupContracts = (list, grouped) => {
  if (!grouped) return [{ key: "all", items: list }];
  const map = new Map();
  list.forEach((c) => {
    if (!map.has(c.client_id)) map.set(c.client_id, { key: c.client_id, items: [] });
    map.get(c.client_id).items.push(c);
  });
  return [...map.values()];
};

const GroupHeader = ({ g, name, city, open, onToggle }) => {
  const days = g.items.reduce((s, c) => s + (Number(c.total_days) || 0), 0);
  const near = g.items.filter(isDeadlineNear).length;
  return (
    <button type="button" onClick={onToggle} data-testid={`client-group-toggle-${g.key}`}
      className="w-full mb-3 flex items-center gap-3 text-left px-4 py-3 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors">
      <Users className="w-5 h-5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-lg leading-tight">
          {name}{city && <span className="text-sm font-normal text-slate-300"> · {city}</span>}
        </div>
        <div className="text-xs text-slate-300 mt-0.5" data-testid={`client-group-summary-${g.key}`}>
          {g.items.length} {g.items.length === 1 ? "contratto" : "contratti"} · {days} giorni totali
          {near > 0 && <span className="text-red-300 font-semibold"> · {near} in scadenza</span>}
        </div>
      </div>
      <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
    </button>
  );
};

export default function Contracts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [ivs, setIvs] = useState([]);
  const [view, setView] = useState("contracts"); // "contracts" | "by-client" | "grouped"
  const [collapsed, setCollapsed] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const csvRef = useRef(null);
  const [sortKey, setSortKey] = useState("deadline");
  const [sortDir, setSortDir] = useState("asc");

  const _PRIO_RANK = { high: 3, medium: 2, low: 1 };
  const sortedItems = [...items].sort((a, b) => {
    let av, bv;
    if (sortKey === "client_name") {
      // Inline lookup to avoid TDZ with clientName() defined later in file
      const na = clients.find((c) => c.id === a.client_id)?.name || "";
      const nb = clients.find((c) => c.id === b.client_id)?.name || "";
      av = na; bv = nb;
    }
    else if (sortKey === "priority") { av = _PRIO_RANK[a.priority] ?? 0; bv = _PRIO_RANK[b.priority] ?? 0; }
    else if (sortKey === "total_days" || sortKey === "daily_rate") { av = Number(a[sortKey] ?? 0); bv = Number(b[sortKey] ?? 0); }
    else { av = a[sortKey] ?? ""; bv = b[sortKey] ?? ""; }
    if (typeof av === "number" && typeof bv === "number") {
      const cmp = av - bv;
      return sortDir === "asc" ? cmp : -cmp;
    }
    const cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase(), "it", { numeric: true, sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const load = () => Promise.all([listContracts(), listClients(), listInterventions()])
    .then(([cs, cls, iv]) => { setItems(cs); setClients(cls); setIvs(iv); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleImportCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await importContractsCsv(file);
      toast.success(`${r.imported} preventivi importati${r.errors?.length ? ` · ${r.errors.length} errori` : ""}`);
      if (r.errors?.length) {
        r.errors.slice(0, 3).forEach((msg) => toast.error(msg));
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import CSV fallito"); }
    finally { setImporting(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  const handleImportPdf = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF troppo grande (max 15MB)"); return; }
    setImporting(true);
    try {
      const r = await aiAnalyzeContractPdf(file);
      const d = r.extracted || {};
      // Find or create client from extracted data
      let clientId = "";
      if (d.client_name) {
        const cls = await listClients();
        setClients(cls);
        let match = cls.find((c) => c.name.toLowerCase() === d.client_name.toLowerCase());
        if (!match) {
          match = await createClient({
            name: d.client_name,
            address: d.address || d.city || "N/D",
            city: d.city || "",
            contact_name: d.contact_name || "",
            phone: d.phone || "",
            email: d.email || "",
            notes: "",
          });
          toast.success(`Cliente "${match.name}" creato automaticamente`);
          const fresh = await listClients();
          setClients(fresh);
        }
        clientId = match.id;
      }
      setEditId(null);
      setForm({
        numero_preventivo: "",
        client_id: clientId,
        title: d.title || "",
        short_description: d.short_description || "",
        total_days: Number(d.total_days) || 1,
        daily_rate: Number(d.daily_rate) || 0,
        intervention_type: d.intervention_type || "consulenza",
        priority: ["high", "medium", "low"].includes(d.priority) ? d.priority : "medium",
        max_per_month: null,
        start_date: d.start_date || "",
        deadline: d.deadline || "",
        signed_date: d.signed_date || "",
        notes: d.notes || "",
        must_be_consecutive: false,
      });
      setOpen(true);
      toast.success(`PDF letto (${r.pages || "?"} pagine) - controlla i dati e salva`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Estrazione PDF fallita");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const clientCity = (id) => clients.find((c) => c.id === id)?.city || "";

  const submit = async () => {
    if (!form.client_id || !form.title || !form.total_days) { toast.error("Cliente, titolo e giorni obbligatori"); return; }
    const payload = {
      ...form,
      total_days: Number(form.total_days) || 0.5,
      daily_rate: Number(form.daily_rate) || 0,
      start_date: form.start_date || null,
      deadline: form.deadline || null,
      signed_date: form.signed_date || null,
    };
    try {
      if (editId) { await updateContract(editId, payload); toast.success("Contratto aggiornato"); }
      else { await createContract(payload); toast.success("Contratto creato"); }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (_e) { toast.error("Errore salvataggio"); }
  };

  const edit = (c) => {
    setEditId(c.id);
    setForm({
      numero_preventivo: c.numero_preventivo || "",
      client_id: c.client_id, title: c.title, short_description: c.short_description || "", total_days: c.total_days, daily_rate: c.daily_rate,
      intervention_type: c.intervention_type, priority: c.priority,
      max_per_month: c.max_per_month || null,
      start_date: c.start_date || "", deadline: c.deadline || "", signed_date: c.signed_date || "",
      notes: c.notes || "",
      must_be_consecutive: !!c.must_be_consecutive,
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!confirm("Eliminare contratto e piano associato?")) return;
    await deleteContract(id); toast.success("Eliminato"); load();
  };

  const doDuplicate = async (id) => {
    try {
      const copy = await duplicateContract(id);
      toast.success(`Contratto duplicato: "${copy.title}"`);
      await load();
      // Apri subito il dialog di modifica sul nuovo contratto
      edit(copy);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore duplicazione");
    }
  };

  const doComplete = async (id) => {
    await completeContract(id); toast.success("Contratto completato"); load();
  };

  const resetPlan = async (id) => {
    if (!confirm("Rimuovere gli interventi pianificati?")) return;
    await clearContractPlan(id); toast.success("Piano azzerato"); load();
  };

  return (
    <div data-testid="contract-manager-section" className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-medium tracking-wider uppercase text-slate-500">Gestione</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold font-display text-slate-900">Contratti</h1>
          <div className="mt-3 inline-flex rounded-md bg-slate-100 p-1 text-xs">
            <button
              data-testid="view-contracts"
              onClick={() => setView("contracts")}
              className={`px-3 py-1.5 rounded font-medium transition-colors ${view === "contracts" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              <FileText className="w-3.5 h-3.5 inline mr-1" /> Vista contratti
            </button>
            <button
              data-testid="view-by-client"
              onClick={() => setView("by-client")}
              className={`px-3 py-1.5 rounded font-medium transition-colors ${view === "by-client" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              <Users className="w-3.5 h-3.5 inline mr-1" /> Agenda per cliente
            </button>
            <button
              data-testid="view-grouped"
              onClick={() => setView("grouped")}
              className={`px-3 py-1.5 rounded font-medium transition-colors ${view === "grouped" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              <Layers className="w-3.5 h-3.5 inline mr-1" /> Raggruppati per cliente
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="contract-pdf-input"
            onChange={(e) => handleImportPdf(e.target.files?.[0])}
          />
          <input
            ref={csvRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="contract-csv-input"
            onChange={(e) => handleImportCsv(e.target.files?.[0])}
          />
          <Button
            data-testid="contracts-template-button"
            variant="outline"
            onClick={() => window.open(contractsTemplateCsvUrl(), "_blank")}
          >
            <FileDown className="w-4 h-4 mr-2" />
            Modello CSV
          </Button>
          <Button
            data-testid="import-contracts-csv-button"
            variant="outline"
            onClick={() => csvRef.current?.click()}
            disabled={importing}
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importa CSV
          </Button>
          <Button
            data-testid="import-contract-pdf-button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
          >
            <Upload className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button
            data-testid="plan-matrix-button"
            variant="outline"
            onClick={() => window.open(planMatrixUrl(new Date().getFullYear()), "_blank")}
            title="Scarica il piano annuale in Excel con la matrice contratti/mesi"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Piano annuale
          </Button>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-contract-button" className="bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="w-4 h-4 mr-2" /> Nuovo contratto
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white sm:max-w-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Modifica contratto" : "Nuovo contratto"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>N° Preventivo</Label>
                  <Input data-testid="contract-number-input" value={form.numero_preventivo} onChange={(e) => setForm({ ...form, numero_preventivo: e.target.value })} placeholder="PREV-2026-001" />
                </div>
                <div className="col-span-2">
                  <Label>Cliente *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="contract-client-select" className="bg-white"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} — {c.city || c.address}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Titolo attività *</Label>
                <Input data-testid="contract-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="es. Migrazione ERP + formazione utenti" />
              </div>
              <div>
                <Label>Descrizione breve servizio</Label>
                <Input data-testid="contract-short-desc-input" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} placeholder="es. Consulenza ISO 17025 · fase audit" maxLength={200} />
                <div className="text-xs text-slate-500 mt-1">Testo breve mostrato nel calendario sotto data e orario. Se vuoto usa il titolo completo.</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Giorni totali *</Label>
                  <Input data-testid="contract-days-input" type="number" min="0.5" step="0.5" value={form.total_days} onChange={(e) => setForm({ ...form, total_days: e.target.value })} />
                  <div className="text-xs text-slate-500 mt-1">Anche decimali (es. 3.5 = 3 giornate + una mattinata). Letto automaticamente dal PDF preventivo.</div>
                </div>
                <div>
                  <Label>Tariffa giornaliera €</Label>
                  <Input data-testid="contract-rate-input" type="number" min="0" step="10" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} />
                  <div className="text-xs text-slate-500 mt-1">Da preventivo o manuale</div>
                </div>
                <div>
                  <Label>Priorità</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="contract-priority-select" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="low">Bassa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Tipologia intervento</Label>
                <Input data-testid="contract-type-input" value={form.intervention_type} onChange={(e) => setForm({ ...form, intervention_type: e.target.value })} placeholder="es. Consulenza, Formazione, Audit…" />
              </div>
              <div>
                <Label>Max interventi / mese</Label>
                <Input data-testid="contract-maxpm-input" type="number" min="0" value={form.max_per_month || ""} onChange={(e) => setForm({ ...form, max_per_month: e.target.value ? Number(e.target.value) : null })} placeholder="Nessun limite" />
                <div className="text-xs text-slate-500 mt-1">La fascia oraria (mattina/pomeriggio/intera) si sceglie in Planning al momento della programmazione</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Firmato il</Label>
                  <Input data-testid="contract-signed-input" type="date" value={form.signed_date} onChange={(e) => setForm({ ...form, signed_date: e.target.value })} />
                </div>
                <div>
                  <Label>Inizio previsto</Label>
                  <Input data-testid="contract-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div>
                  <Label>Scadenza</Label>
                  <Input data-testid="contract-deadline-input" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
                </div>
              </div>
              <div className="flex items-start justify-between gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex-1">
                  <Label htmlFor="must-consecutive" className="font-semibold text-slate-900 cursor-pointer">Giorni consecutivi obbligatori</Label>
                  <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                    Se attivo, l'algoritmo pianifica <b>tutti</b> i giorni di questo contratto in sequenza (giorni lavorativi, saltando week-end e festività) a partire dalla <b>data di inizio</b>. Utile per cantieri che richiedono visita in giorni di fila (es. 2 giorni consecutivi) o per interventi che non ammettono interruzioni. Quando OFF, cadenza mensile standard.
                  </div>
                </div>
                <Switch
                  id="must-consecutive"
                  data-testid="contract-must-consecutive-switch"
                  checked={!!form.must_be_consecutive}
                  onCheckedChange={(v) => setForm({ ...form, must_be_consecutive: v })}
                />
              </div>
              <div>
                <Label>Note</Label>
                <Textarea data-testid="contract-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="contract-save-button" onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white">
                {editId ? "Salva" : "Crea"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {(view === "contracts" || view === "grouped") && (<>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <ArrowUpDown className="w-3.5 h-3.5" />
        <span className="font-medium">Ordina per:</span>
        <Select value={sortKey} onValueChange={setSortKey}>
          <SelectTrigger data-testid="contracts-sort-key" className="h-8 bg-white w-[200px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="deadline">Scadenza</SelectItem>
            <SelectItem value="start_date">Data inizio</SelectItem>
            <SelectItem value="signed_date">Data firma</SelectItem>
            <SelectItem value="title">Titolo attività</SelectItem>
            <SelectItem value="client_name">Cliente</SelectItem>
            <SelectItem value="numero_preventivo">Numero preventivo</SelectItem>
            <SelectItem value="priority">Priorità</SelectItem>
            <SelectItem value="status">Stato</SelectItem>
            <SelectItem value="total_days">Giorni totali</SelectItem>
            <SelectItem value="daily_rate">Tariffa giornaliera</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortDir} onValueChange={setSortDir}>
          <SelectTrigger data-testid="contracts-sort-dir" className="h-8 bg-white w-[140px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="asc">Crescente</SelectItem>
            <SelectItem value="desc">Decrescente</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-slate-400 ml-2">· {sortedItems.length} contratti</span>
      </div>

      {sortedItems.length === 0 && (
        <div className="text-center text-slate-500 py-12">Nessun contratto. Crea il primo.</div>
      )}
      <div className="space-y-6">
      {groupContracts(sortedItems, view === "grouped").map((g) => (
      <div key={g.key} data-testid={g.key === "all" ? undefined : `client-group-${g.key}`}>
        {g.key !== "all" && (
          <GroupHeader g={g} name={clientName(g.key)} city={clientCity(g.key)}
            open={!collapsed[g.key]} onToggle={() => setCollapsed({ ...collapsed, [g.key]: !collapsed[g.key] })} />
        )}
      {!collapsed[g.key] && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {g.items.map((c) => (
          <Card key={c.id} data-testid={`contract-card-${c.id}`} className="p-5 card-hover border-slate-200 bg-white">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  {c.numero_preventivo ? (
                    <span className="font-mono text-xs text-slate-500">{c.numero_preventivo}</span>
                  ) : (
                    <span className="text-xs text-slate-400 italic">senza numero preventivo</span>
                  )}
                </div>
                <div className="flex items-start gap-1.5" data-testid={`contract-client-${c.id}`}>
                  <Users className="w-4 h-4 text-blue-700 shrink-0 mt-1" />
                  <h3 className="font-bold font-display text-slate-900 text-lg leading-tight">
                    {clientName(c.client_id)}
                    {clientCity(c.client_id) && (
                      <span className="text-sm text-slate-500 font-normal"> · {clientCity(c.client_id)}</span>
                    )}
                  </h3>
                </div>
                <div className="mt-1 text-sm text-slate-700 leading-snug" data-testid={`contract-title-${c.id}`}>{c.title}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-md border font-medium uppercase tracking-wide ${priorityBadge[c.priority]}`}>
                    {c.priority === "high" ? "Alta" : c.priority === "low" ? "Bassa" : "Media"}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium uppercase tracking-wide ${statusBadge[c.status]}`}>
                    {c.status === "active" ? "Attivo" : c.status === "planned" ? "Pianificato" : c.status === "completed" ? "Completato" : "Fatturato"}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium uppercase tracking-wide">
                    {c.total_days} giorni
                  </span>
                  {c.daily_rate > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium uppercase tracking-wide">
                      € {c.daily_rate}/g
                    </span>
                  )}
                </div>
                <div className="mt-3 text-xs text-slate-600 space-y-0.5">
                  <div className="grid grid-cols-3 gap-2" data-testid={`contract-dates-${c.id}`}>
                    {[["Firma", c.signed_date, "signed"], ["Inizio", c.start_date, "start"], ["Fine", c.deadline, "end"]].map(([lbl, v, k]) => {
                      const urgent = k === "end" && isDeadlineNear(c);
                      return (
                      <div key={k} data-testid={`contract-${k}-date-${c.id}`} data-urgent={urgent ? "true" : undefined}
                        className={`px-2 py-1 rounded-md border ${urgent ? "bg-red-50 border-red-300" : !v && k === "start" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-50 border-slate-200"}`}>
                        <div className={`text-[10px] uppercase tracking-wide ${urgent ? "text-red-700 font-semibold" : "text-slate-500"}`}>{lbl}</div>
                        <div className={`font-mono font-semibold ${urgent ? "text-red-700" : "text-slate-900"}`}>{v ? v.split("-").reverse().join("/") : "—"}</div>
                      </div>
                      );
                    })}
                  </div>
                  {c.must_be_consecutive && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 font-medium uppercase tracking-wide">
                      <ArrowUpDown className="w-3 h-3 rotate-90" /> Giorni consecutivi
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button data-testid={`edit-contract-${c.id}`} variant="ghost" size="sm" onClick={() => edit(c)} title="Modifica">
                  <Edit3 className="w-4 h-4" />
                </Button>
                <Button data-testid={`duplicate-contract-${c.id}`} variant="ghost" size="sm" onClick={() => doDuplicate(c.id)} title="Duplica contratto">
                  <Copy className="w-4 h-4 text-blue-700" />
                </Button>
                <Button data-testid={`delete-contract-${c.id}`} variant="ghost" size="sm" onClick={() => remove(c.id)} title="Elimina">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
              {c.status !== "completed" && c.status !== "invoiced" && (
                <Button data-testid={`complete-contract-${c.id}`} variant="outline" size="sm" onClick={() => doComplete(c.id)} className="text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Segna completato
                </Button>
              )}
              {c.status === "planned" && (
                <Button data-testid={`reset-plan-${c.id}`} variant="outline" size="sm" onClick={() => resetPlan(c.id)} className="text-xs">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset piano
                </Button>
              )}
              <Button
                data-testid={`fatturapa-${c.id}`}
                variant="outline"
                size="sm"
                onClick={() => {
                  const num = prompt("Numero fattura (per FatturaPA)", `${new Date().getFullYear()}-001`);
                  if (num !== null) window.open(exportFatturaPaUrl(c.id, num), "_blank");
                }}
                className="text-xs"
              >
                <FileCode className="w-3.5 h-3.5 mr-1" /> FatturaPA XML
              </Button>
            </div>
          </Card>
        ))}
      </div>
      )}
      </div>
      ))}
      </div>
      </>)}

      {view === "by-client" && (() => {
        const todayIso = new Date().toISOString().slice(0, 10);
        // Group interventions by client, only for clients that have contracts
        const clientsWithContracts = clients.filter((cli) => items.some((c) => c.client_id === cli.id));
        if (clientsWithContracts.length === 0) {
          return <div className="text-center text-slate-500 py-12">Nessun cliente con contratti attivi.</div>;
        }
        const contractById = Object.fromEntries(items.map((c) => [c.id, c]));
        return (
          <div className="space-y-5" data-testid="agenda-by-client">
            {clientsWithContracts.map((cli) => {
              const clientIvs = ivs.filter((iv) => iv.client_id === cli.id);
              const future = clientIvs.filter((iv) => iv.date >= todayIso).sort((a, b) => a.date.localeCompare(b.date));
              const past = clientIvs.filter((iv) => iv.date < todayIso).sort((a, b) => b.date.localeCompare(a.date));
              const clientContracts = items.filter((c) => c.client_id === cli.id);
              const AgendaRow = ({ iv }) => {
                const c = contractById[iv.contract_id] || {};
                const goToCalendar = () => navigate(`/calendar?open=${iv.id}`);
                return (
                  <tr
                    data-testid={`agenda-row-${iv.id}`}
                    onClick={goToCalendar}
                    className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors"
                    title="Apri appuntamento nel calendario"
                  >
                    <td className="py-2 pr-3 font-mono text-slate-700">{iv.date}</td>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-slate-900">{c.title || "—"}</div>
                      {c.short_description && <div className="text-[10px] text-slate-500 truncate max-w-[380px]">{c.short_description}</div>}
                    </td>
                    <td className="py-2 pr-3 text-center">{c.total_days ?? "—"}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-md border font-medium uppercase tracking-wide ${priorityBadge[c.priority] || ""}`}>
                        {c.priority === "high" ? "Alta" : c.priority === "low" ? "Bassa" : "Media"}
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-slate-700">{c.deadline || "—"}</td>
                  </tr>
                );
              };
              return (
                <Card key={cli.id} data-testid={`agenda-client-${cli.id}`} className="p-5 border-slate-200 bg-white">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-700" />
                      </div>
                      <div>
                        <div className="font-bold font-display text-slate-900 text-lg leading-tight">{cli.name}</div>
                        <div className="text-xs text-slate-500">{cli.city || "—"}{cli.provincia ? ` (${cli.provincia})` : ""}</div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-3">
                      <span><b className="text-slate-900">{clientContracts.length}</b> contratti</span>
                      <span className="text-slate-300">·</span>
                      <span><b className="text-blue-700">{future.length}</b> pianificati</span>
                      <span className="text-slate-300">·</span>
                      <span><b className="text-slate-700">{past.length}</b> passati</span>
                    </div>
                  </div>
                  <Tabs defaultValue="future">
                    <TabsList className="bg-slate-100">
                      <TabsTrigger data-testid={`tab-future-${cli.id}`} value="future" className="text-xs">
                        <Calendar className="w-3.5 h-3.5 mr-1" /> Pianificati ({future.length})
                      </TabsTrigger>
                      <TabsTrigger data-testid={`tab-past-${cli.id}`} value="past" className="text-xs">
                        Passati ({past.length})
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="future" className="mt-3">
                      {future.length === 0 ? (
                        <div className="text-xs text-slate-500 py-4 text-center">Nessun appuntamento pianificato.</div>
                      ) : (
                        <div className="overflow-auto rounded-md border border-slate-100">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-50">
                              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                                <th className="py-2 px-3">Data</th>
                                <th className="py-2 pr-3">Contratto</th>
                                <th className="py-2 pr-3 text-center">Gg. tot.</th>
                                <th className="py-2 pr-3">Priorità</th>
                                <th className="py-2 pr-3">Scadenza</th>
                              </tr>
                            </thead>
                            <tbody>
                              {future.map((iv) => <AgendaRow key={iv.id} iv={iv} />)}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </TabsContent>
                    <TabsContent value="past" className="mt-3">
                      {past.length === 0 ? (
                        <div className="text-xs text-slate-500 py-4 text-center">Nessun appuntamento passato.</div>
                      ) : (
                        <div className="overflow-auto rounded-md border border-slate-100">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-50">
                              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                                <th className="py-2 px-3">Data</th>
                                <th className="py-2 pr-3">Contratto</th>
                                <th className="py-2 pr-3 text-center">Gg. tot.</th>
                                <th className="py-2 pr-3">Priorità</th>
                                <th className="py-2 pr-3">Scadenza</th>
                              </tr>
                            </thead>
                            <tbody>
                              {past.map((iv) => <AgendaRow key={iv.id} iv={iv} />)}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </Card>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}
